import React from 'react';
import PropTypes from 'prop-types';
import { useUiV2 } from '../../config/uiVersion';
import LandingPageV2 from '../PublicV2/LandingPageV2';
import { CONTACT_HREF, DEMO_HREF } from './landingLinks';
import './LandingPage.css';

// Public marketing landing page (the "Hauptseite"). Shown pre-login.
// "Sign in" hands off to the existing MSAL login; "Request a demo" opens mail.
// Copy follows B2B-SaaS best practice: category eyebrow + outcome headline
// (<8 words) + one audience→outcome subheadline. Positioning validated
// against equipment-dealer tools (days-to-sell / turn-rate / margin).
function LandingPageV1({ onSignIn, isLoading, error }) {
    return (
        <div className="lp">
            <header className="lp-nav">
                <div className="lp-nav-inner">
                    <div className="lp-brand">
                        {/* Official IronSight wordmark, used 1:1 (unmodified client
                            artwork) directly on the light nav — no plate, no recolor.
                            The Fusable attribution rides alongside as clean type
                            rather than a cramped bitmap byline. */}
                        <img
                            className="lp-wordmark-img"
                            src="/ironsight-wordmark.png"
                            alt="IronSight"
                            width="96"
                            height="26"
                        />
                        <span className="lp-brand-by">by Fusable</span>
                    </div>
                    <nav className="lp-nav-links">
                        <a href="#what">What it does</a>
                        <a href="#how">How it works</a>
                        <a href={CONTACT_HREF}>Contact</a>
                    </nav>
                    <div className="lp-nav-actions">
                        <button className="lp-link-btn" onClick={onSignIn} disabled={isLoading}>
                            {isLoading ? 'Signing in…' : 'Sign in'}
                        </button>
                        <a className="lp-btn lp-btn-primary" href={DEMO_HREF}>Request a demo →</a>
                    </div>
                </div>
            </header>

            <main className="lp-main">
                <section className="lp-hero">
                    <div className="lp-hero-inner">
                        <div className="lp-hero-copy">
                            <span className="lp-badge"><span className="lp-dot" />Built for equipment dealers</span>
                            <h1 className="lp-h1">Price it right.<br />Sell it faster.</h1>
                            <p className="lp-sub">
                                IronSight scores every unit in your yard overnight — an AI price, a
                                days-to-sell forecast, and live carrying-cost margin. Spot aging stock
                                early, reprice with confidence, and turn it before it eats your margin.
                            </p>
                            <div className="lp-cta-row">
                                <a className="lp-btn lp-btn-primary lp-btn-lg" href={DEMO_HREF}>Request a demo →</a>
                                <button className="lp-btn lp-btn-outline lp-btn-lg" onClick={onSignIn} disabled={isLoading}>
                                    {isLoading ? 'Signing in…' : 'Sign in'}
                                </button>
                            </div>
                            {error && <p className="lp-error">Sign-in didn’t go through. Please try again.</p>}

                            <div className="lp-stats">
                                <div className="lp-stat">
                                    <span className="lp-stat-num">Every unit</span>
                                    <span className="lp-stat-label">scored overnight</span>
                                </div>
                                <div className="lp-stat">
                                    <span className="lp-stat-num">10+ signals</span>
                                    <span className="lp-stat-label">fused per unit</span>
                                </div>
                                <div className="lp-stat">
                                    <span className="lp-stat-num">One verdict</span>
                                    <span className="lp-stat-label">ranked, ready to act</span>
                                </div>
                            </div>
                        </div>

                        <div className="lp-hero-visual" aria-hidden="true">
                            <div className="lp-viz-card">
                                <span className="lp-viz-label">Chance sold</span>
                                <svg className="lp-viz-svg" viewBox="0 0 320 200" preserveAspectRatio="none">
                                    <defs>
                                        <linearGradient id="lpFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop className="lp-viz-stop-top" offset="0%" />
                                            <stop className="lp-viz-stop-bottom" offset="100%" />
                                        </linearGradient>
                                    </defs>
                                    {[40, 80, 120, 160].map((y) => (
                                        <line className="lp-viz-grid" key={y} x1="0" y1={y} x2="320" y2={y} />
                                    ))}
                                    <path className="lp-viz-area" d="M0 190 C60 150 90 70 150 55 C210 40 250 30 320 22 L320 200 L0 200 Z" fill="url(#lpFill)" />
                                    <path className="lp-viz-line" d="M0 190 C60 150 90 70 150 55 C210 40 250 30 320 22" />
                                    {[[0, 190], [80, 96], [150, 55], [230, 34], [320, 22]].map(([cx, cy], i) => (
                                        <circle className="lp-viz-node" key={i} cx={cx} cy={cy} r="4" />
                                    ))}
                                </svg>
                                <div className="lp-viz-foot">
                                    <span>Now</span><span>+3mo</span><span>+6mo</span><span>+9mo</span><span>+12mo</span>
                                </div>
                                <div className="lp-viz-chips">
                                    <span className="lp-chip">Median ~78d</span>
                                    <span className="lp-chip">P(sold 90d) 55%</span>
                                    <span className="lp-chip lp-chip-warn">Slow-mover risk</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── What it does ── */}
                <section id="what" className="lp-section">
                    <div className="lp-section-head">
                        <span className="lp-eyebrow">What it does</span>
                        <h2 className="lp-h2">Every unit — priced, forecast, and risk-flagged.</h2>
                        <p className="lp-section-sub">
                            Every night, IronSight turns pricing, sell-speed, margin and demand
                            into four decisions you can act on the next morning.
                        </p>
                    </div>
                    <div className="lp-cards">
                        <article className="lp-card">
                            <span className="lp-card-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" /><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
                                </svg>
                            </span>
                            <h3 className="lp-card-title">Know what every unit is worth</h3>
                            <p className="lp-card-text">
                                An AI-predicted price on every machine, side by side with your ask and
                                the gap flagged — plus a 12-month value curve, so depreciation never
                                catches you off guard.
                            </p>
                        </article>
                        <article className="lp-card">
                            <span className="lp-card-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="13" r="8" /><path d="M12 13V9" /><path d="M12 1h0M9 1h6" />
                                </svg>
                            </span>
                            <h3 className="lp-card-title">See how long it’ll sit — before it does</h3>
                            <p className="lp-card-text">
                                A calibrated days-to-sell forecast — typical range, median, and “% sold
                                by 90 days” — plus a Red / Yellow / Green slow-mover tier, so you catch
                                dead stock while there’s still time to act.
                            </p>
                        </article>
                        <article className="lp-card">
                            <span className="lp-card-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 2 4 6v6c0 5 3.4 7.6 8 10 4.6-2.4 8-5 8-10V6Z" /><path d="M12 8v4M9.5 10.5h5" />
                                </svg>
                            </span>
                            <h3 className="lp-card-title">Protect margin as carrying cost climbs</h3>
                            <p className="lp-card-text">
                                Floor-plan carrying cost and projected margin at the predicted sale date
                                on every unit — plus a live calculator that answers “take the offer or
                                hold?”, factoring in your current buyer quotes.
                            </p>
                        </article>
                        <article className="lp-card">
                            <span className="lp-card-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M3 17l5-5 4 3 6-7" /><path d="M3 21h18" /><circle cx="18" cy="8" r="1.4" fill="currentColor" stroke="none" />
                                </svg>
                            </span>
                            <h3 className="lp-card-title">See why a unit is stalling — not just that it is</h3>
                            <p className="lp-card-text">
                                Real buyer quotes, marketplace performance and website
                                engagement — percentile-ranked against the machine’s true
                                same-make, model &amp; year peers. Price friction or dropping reach? You’ll know.
                            </p>
                        </article>
                    </div>
                    <div className="lp-agent-band">
                        <div className="lp-agent-text">
                            <span className="lp-eyebrow">Ask IronSight</span>
                            <h3 className="lp-agent-title">One verdict across every signal — one click to act.</h3>
                            <p className="lp-card-text">
                                IronSight fuses pricing, sell-speed, quotes, comps and marketplace data into
                                ranked, plain-English recommendations — and a ready-to-paste listing rewrite.
                                Turn any recommendation into a task without leaving the unit.
                            </p>
                        </div>
                        <div className="lp-agent-chips">
                            <span className="lp-chip">Prioritized actions</span>
                            <span className="lp-chip">Peer benchmark</span>
                            <span className="lp-chip">Listing rewrite</span>
                            <span className="lp-chip">→ Task in one click</span>
                        </div>
                    </div>
                </section>

                {/* ── How it works ── */}
                <section id="how" className="lp-section lp-section-alt">
                    <div className="lp-section-head">
                        <span className="lp-eyebrow">How it works</span>
                        <h2 className="lp-h2">From your inventory to a decision — nightly, automatically.</h2>
                    </div>
                    <ol className="lp-steps">
                        <li className="lp-step">
                            <span className="lp-step-num">1</span>
                            <h3 className="lp-step-title">Connect your inventory</h3>
                            <p className="lp-card-text">
                                IronSight reads your live stock and real sold history, then enriches every
                                unit with market listings, buyer quotes and website activity — refreshed nightly.
                            </p>
                        </li>
                        <li className="lp-step">
                            <span className="lp-step-num">2</span>
                            <h3 className="lp-step-title">The models score every unit</h3>
                            <p className="lp-card-text">
                                Each night, a pricing model and a calibrated survival model score every
                                machine — market value, 12-month trajectory, days-to-sell range and
                                slow-mover risk. No manual comps, no spreadsheets.
                            </p>
                        </li>
                        <li className="lp-step">
                            <span className="lp-step-num">3</span>
                            <h3 className="lp-step-title">Decide, then assign the work</h3>
                            <p className="lp-card-text">
                                It all lands in one dashboard: price, forecast, risk and margin per unit.
                                Reprice aging stock, decide hold-vs-sell, and turn any recommendation into a task.
                            </p>
                        </li>
                    </ol>
                    <div className="lp-how-cta">
                        <a className="lp-btn lp-btn-primary lp-btn-lg" href={DEMO_HREF}>Request a demo →</a>
                        <button className="lp-btn lp-btn-outline lp-btn-lg" onClick={onSignIn} disabled={isLoading}>
                            {isLoading ? 'Signing in…' : 'Sign in'}
                        </button>
                    </div>
                </section>
            </main>

            <footer className="lp-footer">
                <span>© 2026 Fusable. All rights reserved.</span>
                <span className="lp-footer-tag">Predictive pricing &amp; inventory intelligence</span>
            </footer>
        </div>
    );
}

LandingPageV1.propTypes = {
    onSignIn: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.object,
};

// The "new design" flag (config/uiVersion.js). Nobody is signed in here, so it
// comes from ?ui=v2 in the address or the choice remembered in localStorage;
// both are resolved before the app mounts (index.js).
function LandingPage({ onSignIn, isLoading = false, error = null }) {
    const uiV2 = useUiV2();
    const Page = uiV2 ? LandingPageV2 : LandingPageV1;
    return <Page onSignIn={onSignIn} isLoading={isLoading} error={error} />;
}

LandingPage.propTypes = LandingPageV1.propTypes;

export default LandingPage;
